import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { ModelContratoFinanceiroLista } from "../../domain/models/contrato_financeiro_lista";
import { initialFinanceiroContratosFiltersValues, useFinanceiroContratosListaFilters } from "../hooks/financeiro_contratos_lista/filters";
import { useFinanceiroContratosListaPagination } from "../hooks/financeiro_contratos_lista/pagination";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

type LoadContratoFinanceiroListaState = {
    idsCliente?: number[]
    idsCentroCusto?: number[]
    status?: number[]
    descricao?: string | null;
    codigo?: number | null;
    dataInicioDe?: Date | null;
    dataInicioAte?: Date | null;
    dataReceberDe?: Date | null;
    dataReceberAte?: Date | null;

    page?: number;
    rowsPerPage?: number;

}


export const useContratoFinanceiroListaViewModel = () => {

    const {
        reports: {
            getReportContratoFinanceiroListaUseCase,
        },
        financeiro: {
            getAllContratoFinanceiroUseCase,
            updateContratoFinanceiroUseCase,
            deleteContratoFinanceiroUseCase,

        },
        commons: {
            getCentroCustoDropDownUseCase,
            getClientesDropDownUseCase,
        },

    } = useDependencyInjection();

    const navigate = useNavigate();
    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const debouncedRef = useCreateDebounceHandler(1500);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedIdsContratos, setSelectedIdsContratos] = useState<number[]>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [financeiroContratosLista, setFinanceiroContratosLista] = useState<ModelContratoFinanceiroLista[]>();

    const filters = useFinanceiroContratosListaFilters();
    const pagination = useFinanceiroContratosListaPagination();

    //DROPDOWN ESTADOS

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlCliente, setDdlCliente] = useState<DropDown[]>();


    //#region CARREGAMENTO.


    const loadContratosFinanceirosLista = useCallback(async (funcState?: LoadContratoFinanceiroListaState) => {

        try {

            const filteredCodigo = evaluateParamValue(funcState?.codigo, filters.codigo)
            const filteredDescricao = evaluateParamValue(funcState?.descricao, filters.descricao)
            const filteredDataInicioDe = evaluateParamValue(funcState?.dataInicioDe, filters.dataInicioDe);
            const filteredDataInicioAte = evaluateParamValue(funcState?.dataInicioAte, filters.dataInicioAte);
            const filteredDatarDe = evaluateParamValue(funcState?.dataReceberDe, filters.dataReceberDe);
            const filteredDataAte = evaluateParamValue(funcState?.dataReceberAte, filters.dataReceberAte);
            const filteredIdsCliente = funcState?.idsCliente ?? filters.idsCliente;
            const filteredIdsCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredStatus = funcState?.status ?? filters.status;


            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllContratoFinanceiroUseCase.getAllContratoFinanceiro(
                filteredIdsCliente ?? [],
                filteredIdsCentroCusto ?? [],
                filteredDescricao,
                filteredCodigo,
                filteredDatarDe,
                filteredDataAte,
                filteredStatus ?? [],
                filteredDataInicioDe,
                filteredDataInicioAte,
                filteredPage,
                filteredRowsPerPage

            )
            setFinanceiroContratosLista(getAllContratoFinanceiroUseCase.items);

            pagination.setRowsPerPage(getAllContratoFinanceiroUseCase.pageSize);
            pagination.setTotalItems(getAllContratoFinanceiroUseCase.totalItems);
        } catch (e: unknown) {
            setFinanceiroContratosLista([]);
            pushErrorNotification('Error', processError(e, 'Falha ao buscar Contratos!'));
        }
    }, [
        filters,
        pagination,
        getAllContratoFinanceiroUseCase,
        pushErrorNotification,
    ]);

    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase, pushErrorNotification, setPageLoading]);

    const loadClientesDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getClientesDropDownUseCase.getClientesDropDown();
            setDdlCliente(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar clientes!'));
        } finally {
            setPageLoading(false);
        }
    }, [getClientesDropDownUseCase, pushErrorNotification, setPageLoading]);


    //#endregion

    usePageStartBootstrap(async () => {
        try {
            await loadContratosFinanceirosLista()
            await loadCentroCustoDropDown();
            await loadClientesDropDown();

        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de contratos!")[0]);
        }
    })

    const handleFinanceiroContratosEdit = useCallback((idFinanceiroContrato: number | null) => {
        navigate('/FinanceiroContratos_cadastro', { state: { idFinanceiroContrato } });
    }, [navigate]);

    const handleNewFinanceiroContratos = useCallback(() => {
        navigate("/FinanceiroContratos_cadastro");
    }, [navigate]);


    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadContratosFinanceirosLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contratos financeiros!'));
        } finally {
            setLoading(false);
        }
    }, [loadContratosFinanceirosLista, pushErrorNotification]);

    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadContratosFinanceirosLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contratos financeiros!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContratosFinanceirosLista, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadContratosFinanceirosLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contratos financeiros!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContratosFinanceirosLista, pushErrorNotification]);

    //#endregion




    //#region DELECAO
    const handleFinanceiroContratosDeleteOpenState = useCallback((idFinanceiroContratos: number | null, openState: boolean) => {
        if (openState) {
            if (idFinanceiroContratos) {
                setSelectedIdsContratos([idFinanceiroContratos]);
            } else {
                setSelectedIdsContratos(undefined);
            }
        }
        setOpenDeleteModal(openState);
    }, []);

    const handleConfirmarDeletarFinanceiroContratos = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdsContratos && selectedIdsContratos.length > 0)
                await deleteContratoFinanceiroUseCase.deleteContratoFinanceiro(selectedIdsContratos[0]);

            pushInfoNotification("Sucesso", "Contrato Deletado com sucesso!", { isAutoFinish: true });
            await loadContratosFinanceirosLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Contas!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdsContratos(undefined)
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        selectedIdsContratos,
        deleteContratoFinanceiroUseCase,
        loadContratosFinanceirosLista,
        pushErrorNotification,
        pushInfoNotification
    ]);


    //#endregion





    //#region PARALISAÇÃO


    const handleParalisarContratos = useCallback(async (paralisar: boolean) => {
        try {
            setSaving(true);

            if (rowSelectionModel) {
                const idsFinanceiroContratos: number[] = [];
                for (let row of rowSelectionModel.ids.values()) {
                    if (typeof row === 'number') {
                        idsFinanceiroContratos.push(row);
                    } else if (typeof row === 'string') {
                        try {
                            idsFinanceiroContratos.push(Number(row));
                        } catch (e) {
                        }
                    }
                }

                if (paralisar) {
                    // Mapeia os IDs para um array de Promises
                    const updates = idsFinanceiroContratos.map(e => {
                        return updateContratoFinanceiroUseCase.updateContratoFinanceiro({
                            idContratoFinanceiro: e,
                            status: 3
                        });
                    });
                    await Promise.all(updates);

                } else {
                    const updates = idsFinanceiroContratos.map(e => {
                        return updateContratoFinanceiroUseCase.updateContratoFinanceiro({
                            idContratoFinanceiro: e,
                            status: 0
                        });
                    });
                    await Promise.all(updates);
                }

                pushInfoNotification("Sucesso", "Contratos alterados com sucesso!", { isAutoFinish: true });
            }

            await loadContratosFinanceirosLista();

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao alterar contratos!"));
        } finally {
            setSaving(false);
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        updateContratoFinanceiroUseCase,
        rowSelectionModel,
        loadContratosFinanceirosLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion





    //#region  RELATÓRIO.

    const handleContratoFinanceiroRelatorio = useCallback(async () => {
        try {
            setLoading(true);

            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContratoFinanceiroListaUseCase.execute(mimeType);
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de Contrato Financeiro!'));
        } finally {
            setLoading(false);
        }
    }, [
        getReportContratoFinanceiroListaUseCase,
        startReportViewerProcess,
        pushErrorNotification,
    ]);

    //#endregion





    //#region PESQUISA.

    const handleIdCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCentroCusto: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContratosFinanceirosLista({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contratos financeiros!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContratosFinanceirosLista,]
    );

    const handleIdClienteChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCliente: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContratosFinanceirosLista({ idsCliente: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contratos financeiros!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContratosFinanceirosLista,]
    );

    const handleStatusChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ status: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContratosFinanceirosLista({ status: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contratos financeiros!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContratosFinanceirosLista,]
    );



    const handleDescricaoChange = useDebouncedCallback(
        debouncedRef,
        (descricao: string | null) => {
            filters.setFilters({ descricao });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContratosFinanceirosLista({ descricao, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contratos financeiros!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContratosFinanceirosLista, filters]
    );


    const handleDataReceberRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataReceberDe: Date | null, dataReceberAte: Date | null) => {
            filters.setFilters({ dataReceberDe });
            filters.setFilters({ dataReceberAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContratosFinanceirosLista({ dataReceberDe, dataReceberAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contratos financeiros!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContratosFinanceirosLista, filters]
    );

    const handleDataInicioRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataInicioDe: Date | null, dataInicioAte: Date | null) => {
            filters.setFilters({ dataInicioDe });
            filters.setFilters({ dataInicioAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContratosFinanceirosLista({ dataInicioDe, dataInicioAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contratos financeiros!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContratosFinanceirosLista, filters]
    );

    const handleCodigoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            const valorNormalizado =
                newValue && !isNaN(Number(newValue)) && newValue.trim() !== ""
                    ? Number(newValue)
                    : null;

            filters.setFilters({ codigo: valorNormalizado });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContratosFinanceirosLista({ codigo: valorNormalizado, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar contratos financeiros!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadContratosFinanceirosLista]
    );


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadContratosFinanceirosLista({
                ...initialFinanceiroContratosFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar contratos financeiros!'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, loadContratosFinanceirosLista, pushErrorNotification]);


    //#endregion 



    return {
        loading,
        saving,
        financeiroContratosLista: financeiroContratosLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlCentroCusto: ddlCentroCusto ?? [],
        ddlCliente: ddlCliente ?? [],
        rowSelectionModel,
        reportViewerState,

        handleStatusChange,
        handleDataInicioRangeChange,
        handleCodigoChange,
        handleContratoFinanceiroRelatorio,
        handleDescricaoChange,
        handleParalisarContratos,
        handleRowSelectionModelChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
        handleFinanceiroContratosEdit,
        handleFinanceiroContratosDeleteOpenState,
        handleNewFinanceiroContratos,
        handleConfirmarDeletarFinanceiroContratos,
        handleIdCentroCustoChange,
        handleIdClienteChange,
        handleDataReceberRangeChange,



    };
}
